import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, Spinner } from "@bphxd/ds-core-react";
import { Remove32, Delete48 } from "@bphxd/ds-core-react/lib/icons";
import "./QuestionnairePage.scss";
import { questionnaireFetch, questionnaireSave } from "../../api/endpoints";
import { useRef } from "react";
import { useMsal } from "@azure/msal-react";

type QuestionnairePageProps = {
    initialData?: any;
    onSubmittingStart?: () => void;
    onSubmitSuccess?: () => void;
    onSubmitError?: () => void;
};

type HistoryItem = {
    id: number;
    answer?: any;
    approve: boolean | null;
    remark: string;
    created_by: string;
    created_date: string;
    updated_date: string | null;
    updated_by: string;
};

type Question = {
    id: string;
    value: string;
    answer: string | null;
    approved: boolean | null;
    open?: boolean;
    satisfied?: "YES" | "NO" | null;
    remarks?: string;
    isNew?: boolean;
    isSaved?: boolean;
    question_history?: HistoryItem[];
};

type BoundaryState = {
    approved: boolean;
    id?: number;
    answer: boolean | string;
    satisfied: string;
    remarks: string;
    conditions: string | string[];
    isSaved: boolean;
    boundary_history?: HistoryItem[];
};

type ValidationError = {
    questionId?: string;
    boundary?: boolean;
    message: string;
};

export default function QuestionnairePage({
    initialData,
    onSubmittingStart,
    onSubmitSuccess,
    onSubmitError
}: QuestionnairePageProps) {
    const location = useLocation();
    const data = initialData || location.state;
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [boundaryOpen, setBoundaryOpen] = useState(true);
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const hasFetched = useRef(false);
    const { instance, accounts } = useMsal();
    const accessTokenRequest = {
        account: accounts[0],
        scopes: ["User.Read"],
    };
    const jwtToken = sessionStorage.getItem("jwt");
    const role = sessionStorage.getItem("role");
    const userEmail = accessTokenRequest.account.username;
    const [responseStatus, setResponseStatus] = useState<string>("")
    const [saveMessage, setSaveMessage] = useState("");
    const [boundary, setBoundary] = useState<BoundaryState>({
        id: undefined,
        answer: "",
        satisfied: "",
        remarks: "",
        conditions: "",
        approved: false,
        isSaved: false,
        boundary_history: [],
    });

    // ✅ ROLE-BASED ACCESS CHECK
    const hasReviewAccess = () => role === "itr_user"|| role === "admin";

    // ✅ STATUS HELPER FUNCTIONS
    const isOpen = () => responseStatus === "OPEN";
    const isSubmitted = () => responseStatus === "SUBMITTED";
    const isReSubmitted = () => responseStatus === "RE-SUBMITTED";
    const isRemarked = () => responseStatus === "REMARKED" || responseStatus === "RE-REMARKED";
    const isApproved = () => responseStatus === "APPROVED";

    const shouldShowSatisfyOptions = () => {
        return hasReviewAccess() && (isSubmitted() || isReSubmitted() || isRemarked())&& !isApproved();
    };

    const shouldShowAddButton = () => {
        return hasReviewAccess() && !isOpen() && (isApproved()|| isSubmitted() || isReSubmitted() || isRemarked());
    };

    // ✅ DETERMINE NEXT STATUS
    const determineNextStatus = (): string => {
        // Check if all questions and boundary are satisfied
        const allQuestionsSatisfied = questions.every(q => {
            const hasAnswer = q.answer !== null && q.answer !== "";
            if (!hasAnswer || q.isNew) return false; // Exclude new questions
            return q.satisfied === "YES";
        });

        // const boundaryConditionSatisfied = boundary.answer
        //     ? boundary.satisfied === "YES"
        //     : true; // If no boundary answer, consider it satisfied

        const hasNewQuestions = questions.some(q => q.isNew && q.isSaved);

        
    // Case 1: New questions added (even if all existing were approved) → RE-REMARKED
    if (hasNewQuestions) {
        if (responseStatus === "APPROVED") {
            return "RE-REMARKED";  // Go back to RE-REMARKED if new questions added
        } else if (responseStatus === "SUBMITTED") {
            return "REMARKED";
        } else if (responseStatus === "RE-SUBMITTED") {
            return "RE-REMARKED";
        } else {
            return responseStatus;
        }
    }

    // Case 2: All satisfied, no new questions → APPROVED
    if (allQuestionsSatisfied && !hasNewQuestions) {
        return "APPROVED";
    }

    // Case 3 & 4: Not all satisfied, no new questions → REMARKED/RE-REMARKED
    if (responseStatus === "SUBMITTED") {
        return "REMARKED";
    } else if (responseStatus === "RE-SUBMITTED") {
        return "RE-REMARKED";
    } else {
        return responseStatus;
    }
    };

   // CHECK IF FORM IS READY TO SUBMIT
const isFormValid = () => {
    console.log("Checking form validity...");
    // Allow submission if there are new saved questions (even without answers)
    const hasNewSavedQuestions = questions.some(q => q.isNew && q.isSaved);
    
    const hasQuestionInput = questions.some(q => {
        const hasAnswer = q.answer !== null && q.answer !== "";
        // Skip validation for new questions entirely
        if (q.isNew) return false;
        // For existing questions, check they have answers and satisfaction status
        if (!hasAnswer) return false;
        if (!q.satisfied) return false;
        if (q.satisfied === "NO" && !q.remarks?.trim()) return false;
        return true;
    });

    // const hasBoundaryInput = boundary.answer && boundary.satisfied &&
    //     (boundary.satisfied === "YES" || (boundary.satisfied === "NO" && boundary.remarks?.trim()));

    return hasNewSavedQuestions || hasQuestionInput;
};

const validateForm = (): { isValid: boolean; errors: ValidationError[] } => {
    const errors: ValidationError[] = [];

    // Skip validation if status is APPROVED
    if (isApproved()) {
        return { isValid: true, errors: [] };
    }

    // for Boundary conditions we decided to not show Satisfy/Not Satisfy options hence discarding old data from the history
    // if (boundary.answer) {
    //     if (!boundary.satisfied) {
    //         errors.push({ boundary: true, message: "Boundary condition must be marked as Satisfied or Not Satisfied" });
    //     } else if (boundary.satisfied === "NO" && !boundary.remarks?.trim()) {
    //         errors.push({ boundary: true, message: "Remarks are required when boundary condition is Not Satisfied" });
    //     }
    // }

    questions.forEach((q) => {
        const hasAnswer = q.answer !== null && q.answer !== "";
        if (hasAnswer && !q.isNew) {
            if (!q.satisfied) {
                errors.push({ questionId: q.id, message: `Question "${q.value}" must be marked as Satisfied or Not Satisfied` });
            } else if (q.satisfied === "NO" && !q.remarks?.trim()) {
                errors.push({ questionId: q.id, message: `Remarks are required for "${q.value}" when marked as Not Satisfied` });
            }
        }
    });

    return { isValid: errors.length === 0, errors };
};
    // FETCH API
    useEffect(() => {
        if (!data?.order_number) return;
        if (hasFetched.current) return;
        hasFetched.current = true;

        const fetchData = async () => {
            try {
                setIsLoading(true);

                const url = `${questionnaireFetch}?searchField=order_number&searchVal=${data.order_number}&country_name=${data.country_name}`;

                const res = await fetch(url, {
                    method: 'get',
                    headers: {
                        'x-api-key': "gmedocumenthubtest.castrol.com",
                        'Content-Type': 'application/json',
                        AuthorizationToken: `Bearer ${jwtToken}`
                    },
                });
                const result = await res.json();

                const countryData = result?.data?.[0]?.Country?.[0];
                const responseStatus = result?.data?.[0]?.response_status;
                setResponseStatus(responseStatus);
                const mapped =
                    countryData?.Questionaries?.map((q: any) => ({
                        id: q.id,
                        value: q.value,
                        answer: q.answer || null,
                        approved: q.approved || null,
                        open: true,
                        satisfied: ((responseStatus === "REMARKED" || responseStatus === 'RE-REMARKED' || responseStatus === "SUBMITTED" || responseStatus === 'RE-SUBMITTED'
                        ) && q.approved === true)
                            ? "YES" : q.approved === false ?
                                "NO" : "",
                        remarks: q.remarks || "",
                        isSaved: responseStatus === "REMARKED" || responseStatus === 'RE-REMARKED' ? true : false,
                        question_history: q.question_history || [],
                    })) || [];
                console.log("Mapped Questions:", mapped);
                setQuestions(mapped);

                if (countryData?.Boundry_condition) {
                    const bc = countryData.Boundry_condition;
                    setBoundary({
                        id: bc.id,
                        conditions: bc.value,
                        approved: bc.approved || false,
                        answer: bc.answer === true ? "YES" : bc.answer === false ? "NO" : "",
                        satisfied: ((responseStatus === "REMARKED" || responseStatus === "RE-REMARKED" || responseStatus === "SUBMITTED" || responseStatus === 'RE-SUBMITTED'
                        ) && bc.approved === true)
                            ? "YES"
                            : bc.approved === false
                                ? "NO"
                                : "",
                        remarks: bc.remarks || "",
                        isSaved: responseStatus === "REMARKED" || responseStatus === 'RE-REMARKED'? true : false,
                        boundary_history: bc.boundary_history || [],
                    });
                    console.log("Boundary Condition:", bc);
                }
            } catch (err) {
                if (err instanceof Error && err.name === 'AbortError') {
                    console.log("Fetch cancelled");
                } else {
                    console.error("API Error:", err);
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [data?.order_number, data?.country_name, jwtToken]);

    // TOGGLE
    const toggleOpen = (id: string) => {
        setQuestions((prev) =>
            prev.map((q) =>
                q.id === id ? { ...q, open: !q.open } : q
            )
        );
    };

    // UPDATE FIELD
    const updateField = (id: string, key: string, value: any) => {
        setQuestions((prev) =>
            prev.map((q) =>
                q.id === id ? { ...q, [key]: value } : q
            )
        );
        setValidationErrors((prev) => prev.filter(err => err.questionId !== id));
    };

    const updateBoundary = (key: string, value: any) => {
        setBoundary((prev) => ({
            ...prev,
            [key]: value,
            isSaved: false
        }));
    };

    const handlenewSave = (id: string) => {
        const q = questions.find((q) => q.id === id);

        if (!q?.value.trim()) {
            alert("Question cannot be empty");
            return;
        }

        setQuestions((prev) =>
            prev.map((q) =>
                q.id === id
                    ? { ...q, isNew: true, isSaved: true }
                    : q
            )
        );

        setSaveMessage("Question saved successfully. Click Submit to send it to the customer.");

        setTimeout(() => {
            setSaveMessage("");
        }, 4000);
    };

    const handleAdd = () => {
        setQuestions((prev) => [
            ...prev,
            {
                id: Date.now().toString(),
                value: "",
                answer: null,
                approved: null,
                isNew: true,
                isSaved: false,
                satisfied: null,
                remarks: "",
                question_history: [],
            },
        ]);
    };

    // DELETE
    const handleDelete = (id: string) => {
        setQuestions((prev) => prev.filter((q) => q.id !== id));
        setValidationErrors((prev) => prev.filter(err => err.questionId !== id));
    };

    // ✅ SUBMIT WITH VALIDATION & CALLBACKS
const handleSubmit = async () => {
    console.log("Submitting form...",questions, boundary);
    const { isValid, errors } = validateForm();
    console.log("Validation Result:", { isValid, errors });
    if (!isValid) {
        setValidationErrors(errors);
        return;
    }

    const nextStatus = determineNextStatus();

    const payload = {
        order_number: data.order_number,
        country_name: data.country_name,
        vessel_imo_number: data.vessel_imo_number,
        vessel_name: data.vessel_name,
        customer_code: data.cust_code,
        cust_acct_name: data.cust_acct_name,
        user_email: userEmail,
        response_status: nextStatus,
        questions: questions
        .filter(q => q.isNew || q.approved !== true)// ✅ Exclude satisfied questions (unless they're new)
            .map((q) => ({
                id: q.id,
                value: q.value,
                isNew: q.isNew || false,
                satisfied: q.satisfied,
                remarks: q.remarks,
            })),
      // ✅ Only include boundary if not already approved // removed boundary as satisfied/unsatisfied flow is removed
    //   ...(boundary.approved === true ? {} : {
    //     boundary: {
    //         id: boundary.id,
    //         value: boundary.conditions,
    //         answer: boundary.answer,
    //         satisfied: boundary.satisfied === "YES",
    //         remarks: boundary.satisfied === "YES" ? "" : boundary.remarks,
    //     }
    // })
    };

    try {
        setIsSubmitting(true);
        onSubmittingStart?.();
        await new Promise(resolve => setTimeout(resolve, 2000));
        const response = await fetch(questionnaireSave, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                AuthorizationToken: `Bearer ${jwtToken}`
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

       const result = await response.json();
        console.log("FINAL SUBMIT:", payload);
        console.log("Next Status:", nextStatus);

        onSubmitSuccess?.();

    } catch (err) {
        console.error("Submit Error:", err);
        alert("Error submitting form. Please try again.");
        onSubmitError?.();
    } finally {
        setIsSubmitting(false);
    }
};

    const HistoryDisplay = ({ isBoundaryCondition = false, history }: { isBoundaryCondition?: Boolean, history: HistoryItem[] }) => {
        if (!history || history.length === 0) return null;

        let sortedHistory = [...history].sort(
            (a, b) =>
                new Date(b.created_date).getTime() -
                new Date(a.created_date).getTime()
        );
        // for Boundary conditions we decided to not show Satisfy/Not Satisfy options hence discarding old data from the history
        if(isBoundaryCondition) sortedHistory = [sortedHistory[0]];

        return (
            <div
                style={{
                    marginTop: '15px',
                    padding: '10px',
                    backgroundColor: '#e8f4f8',
                    borderRadius: '4px',
                    borderLeft: '4px solid #0066cc'
                }}
            >
                <strong style={{ color: '#0066cc' }}>📋 History:</strong>

                {sortedHistory.map((item, idx) => (
                    <div
                        key={idx}
                        style={{
                            marginTop: '10px',
                            fontSize: '12px',
                            paddingBottom: '10px',
                            borderBottom:
                                idx < sortedHistory.length - 1
                                    ? '1px solid #ccc'
                                    : 'none'
                        }}
                    >
                        {/* ✅ Updated Section */}
                        {(item.updated_date || item.updated_by) &&
                            typeof item.approve === "boolean" && (
                                <div
                                    style={{
                                        marginLeft: "8px",
                                        padding: '8px',
                                        backgroundColor: '#fff3e0',
                                        borderRadius: '3px',
                                        borderLeft: '3px solid #ff9800'
                                    }}
                                >
                                    <div
                                        style={{
                                            fontWeight: '500',
                                            marginBottom: '5px'
                                        }}
                                    >
                                        <strong>Status:</strong>{" "}
                                        {item.approve === true
                                            ? '✅ Satisfied'
                                            : '❌ Not Satisfied'}
                                    </div>

                                    {item.remark && (
                                        <div style={{ marginBottom: '5px' }}>
                                            <strong>Remark:</strong> {item.remark}
                                        </div>
                                    )}

                                    <div
                                        style={{
                                            color: '#e65100',
                                            fontWeight: '500'
                                        }}
                                    >
                                        🔄 Updated:
                                    </div>

                                    <div>
                                        <strong>By:</strong>{" "}
                                        {item.updated_by || item.created_by}
                                    </div>

                                    <div>
                                        <strong>Date:</strong>{" "}
                                        {item.updated_date
                                            ? new Date(item.updated_date).toLocaleString('en-GB', {
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                second: '2-digit',
                                                hour12: false,
                                                timeZone: 'UTC'
                                            }) + "(UTC)"
                                            : 'N/A'}
                                    </div>
                                </div>
                            )}

                        {/* ✅ Created Section */}
                        <div
                            style={{
                                marginBottom: '8px',
                                padding: '8px',
                                backgroundColor: '#e8f5e9',
                                borderRadius: '3px'
                            }}
                        >
                            {(item.answer !== undefined &&
                                item.answer !== null) && (
                                    <div
                                        style={{
                                            marginBottom: '8px',
                                            padding: '8px',
                                            backgroundColor: '#f0f8ff',
                                            borderRadius: '3px',
                                            borderLeft:
                                                '3px solid #0066cc'
                                        }}
                                    >
                                        <strong>Answer:</strong>{" "}
                                        {item.answer === true ||
                                            item.answer === "true"
                                            ? "YES, boundary conditions are met"
                                            : item.answer === false ||
                                                item.answer === "false"
                                                ? "NO, boundary conditions are not met"
                                                : item.answer}

                                        <div>
                                            <div
                                                style={{
                                                    color: '#2e7d32',
                                                    fontWeight: '500'
                                                }}
                                            >
                                                📝 Created:
                                            </div>

                                            <strong>By:</strong>{" "}
                                            {item.created_by}
                                        </div>

                                        <div>
                                            <strong>Date:</strong>{" "}
                                            {new Date(
                                                item.created_date
                                            ).toLocaleString('en-GB', {
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                second: '2-digit',
                                                hour12: false,
                                                timeZone: 'UTC'
                                            }) + "(UTC)"}
                                        </div>
                                    </div>
                                )}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    if (!data) return (
        <div style={{ height: "300px", width: "100%", textAlign: "center" }} className="no-Data-div">
            No data available
        </div>
    );

    if (isLoading) {
        return (
            <div className="loader">
                <Spinner color="brand" />
            </div>
        );
    }

    const conditionsArray = Array.isArray(boundary?.conditions)
        ? boundary.conditions
        : typeof boundary?.conditions === "string"
            ? boundary.conditions.split("\n").filter(Boolean)
            : [];

    return (
        <div>
            {/* HEADER */}
            <div className="top-header">
                Order: {data.order_number} | Vessel IMO: {data.vessel_imo_number} |
                Vessel: {data.vessel_name} | Customer Code: {data.cust_code} |
                Customer: {data.cust_acct_name} | Country: {data.country_name}
            </div>

            <div className="page">
                {/* BOUNDARY CONDITION */}
                {conditionsArray.length > 0 && <div className="card">
                    <div className="header" onClick={() => setBoundaryOpen(!boundaryOpen)}>
                        Boundary Condition
                        <span>{boundaryOpen ? "▲" : "▼"}</span>
                    </div>
                    {boundaryOpen && (
                        <div className="body">
                            {Array.isArray(conditionsArray) ?
                                conditionsArray.map((c, i) => (
                                    <p key={i}>
                                        <strong>{i + 1}.{c}</strong>
                                    </p>
                                ))
                                : <p><strong>{conditionsArray}</strong></p>
                            }
                            {boundary.answer !== "" ? (
                                <>
                                    <div className="radio-group horizontal">
                                        <label>
                                            <input
                                                type="radio"
                                                checked={boundary.answer === "YES" || boundary.answer === 'true'}
                                                disabled={!!boundary.answer}
                                                onChange={() => updateBoundary("answer", "YES")}
                                            />
                                            YES, the boundary conditions are met.
                                        </label>
                                        <label>
                                            <input
                                                type="radio"
                                                checked={boundary.answer === "NO" || boundary.answer === 'false'}
                                                disabled={!!boundary.answer}
                                                onChange={() => updateBoundary("answer", "NO")}
                                            />
                                            NO, the boundary conditions are not met.
                                        </label>
                                    </div>

                                    {/* ✅ Show Satisfy/Not Satisfy only for SUBMITTED, RE-SUBMITTED, REMARKED AND hasReviewAccess */}
                                    {/* {shouldShowSatisfyOptions() && boundary.approved !== true && (
                                        <>
                                            <div className="radio-group horizontal">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        checked={boundary.satisfied === "YES"}
                                                        onChange={() => updateBoundary("satisfied", "YES")}
                                                    />
                                                    Satisfied
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        checked={boundary.satisfied === "NO"}
                                                        onChange={() => updateBoundary("satisfied", "NO")}
                                                    />
                                                    Not Satisfied
                                                </label>
                                            </div>

                                            {boundary.satisfied === "NO" && (
                                                <textarea
                                                    className="remarks"
                                                    placeholder="Enter remarks"
                                                    value={boundary.remarks}
                                                    onChange={(e) => updateBoundary("remarks", e.target.value)}
                                                />
                                            )}
                                        </>
                                    )} */}

                                    {validationErrors.some(err => err.boundary) && (
                                        <div className="validation-error" style={{ color: 'red', fontSize: '14px', marginTop: '10px' }}>
                                            ⚠️ {validationErrors.find(err => err.boundary)?.message}
                                        </div>
                                    )}

                                    {/* Show history only when answer exists */}
                                    {boundary.answer && (
                                        <HistoryDisplay isBoundaryCondition={true} history={boundary.boundary_history || []} />
                                    )}
                                </>
                            ) : (
                                <> Not Answered Yet</>
                            )}
                        </div>
                    )}
                </div>
                }

                {/* QUESTIONS */}
                {questions.map((q) => {
                    const hasAnswer = q.answer !== null && q.answer !== "";
                    const questionErrors = validationErrors.filter(err => err.questionId === q.id);

                    return (
                        <div key={q.id} className="card">
                            {q.isNew && !q.isSaved ? (
                                <div className="body">
                                    < div style={{ display: "flex", alignItems: "center", fontWeight:"bold" }} onClick={() => handleDelete(q.id)}>
                                        Add Your Question
                                        <span style={{ marginLeft: "auto", cursor: "pointer" }}>
                                            <Remove32 color="red" />
                                        </span>
                                    </div>
                                    <div style={{ position: 'relative', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                        <input
                                            className="input"
                                            placeholder="Enter your question"
                                            value={q.value}
                                            onChange={(e) => updateField(q.id, "value", e.target.value)}
                                            style={{ flex: 1 }}
                                        />
                                    </div>
                                    <div className="actions-right">
                                        <Button disabled={!q.value.trim()} onClick={() => handlenewSave(q.id)}>
                                            Save
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="header" onClick={() => toggleOpen(q.id)}>
                                        {q.value || "Question"}
                                        <div style={{ display: "flex", gap: "10px" }}>
                                            {q.isSaved && q.isNew && (
                                                <div onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDelete(q.id);
                                                }}>
                                                    <Delete48 color="red" />
                                                </div>
                                            )}
                                            {!q.isNew && <span>{q.open ? "▲" : "▼"}</span>}
                                        </div>
                                    </div>

                                    {q.open && !q.isNew && (
                                        <div className="body">
                                            {hasAnswer ? (
                                                <div className="answer">{q.answer}</div>
                                            ) : (
                                                <p>Not Answered Yet</p>
                                            )}

                                            {/* ✅ Show Satisfy/Not Satisfy only if hasReviewAccess */}
                                            {hasAnswer && shouldShowSatisfyOptions() && q.approved !== true &&(
                                                <>
                                                    <div className="radio-group horizontal">
                                                        <label>
                                                            <input
                                                                type="radio"
                                                                checked={q.satisfied === "YES"}
                                                                onChange={() => updateField(q.id, "satisfied", "YES")}
                                                            />
                                                            Satisfied
                                                        </label>
                                                        <label>
                                                            <input
                                                                type="radio"
                                                                checked={q.satisfied === "NO"}
                                                                onChange={() => updateField(q.id, "satisfied", "NO")}
                                                            />
                                                            Not Satisfied
                                                        </label>
                                                    </div>

                                                    {q.satisfied === "NO" && (
                                                        <textarea
                                                            className="remarks"
                                                            placeholder="Enter remarks"
                                                            value={q.remarks}
                                                            onChange={(e) => updateField(q.id, "remarks", e.target.value)}
                                                        />
                                                    )}

                                                    {q.remarks && q.satisfied === "NO" && (
                                                        <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                                            <strong>Remarks:</strong> {q.remarks}
                                                        </div>
                                                    )}

                                                    {questionErrors.length > 0 && (
                                                        <div className="validation-error" style={{ color: 'red', fontSize: '14px', marginTop: '10px' }}>
                                                            ⚠️ {questionErrors[0].message}
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {/* Show history only when answer exists */}
                                            {hasAnswer && (
                                                <HistoryDisplay history={q.question_history || []} />
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    );
                })}

                {saveMessage && (
                    <div
                        style={{
                            backgroundColor: "#d4edda",
                            color: "#155724",
                            padding: "10px",
                            marginTop: "15px",
                            borderRadius: "5px",
                            fontWeight: "500"
                        }}
                    >
                        {saveMessage}
                    </div>
                )}

                {/* ACTION BUTTONS */}
                <div className="actions">
                    <div style={{ marginLeft: "auto" }}>
                        {/* ✅ Show Add button only for itr_user role */}
                        {shouldShowAddButton() && (
                            <Button
                                style={{ marginRight: '20px' }}
                                rounded="pill"
                                theme="brand"
                                level="secondary"
                                size="lg"
                                onClick={handleAdd}
                                disabled={isSubmitting}
                            >
                                Add
                            </Button>
                        )}
                        {/* ✅ Submit button only for itr_user role */}
                        {hasReviewAccess() && (
                            <Button
                                rounded="pill"
                                theme="brand"
                                level="primary"
                                size="lg"
                                onClick={handleSubmit}
                                disabled={!isFormValid() || isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <Spinner
                                            color="brand"
                                            style={{ width: '16px', height: '16px', marginRight: '8px', display: 'inline-block' }}
                                        />
                                        Submitting...
                                    </>
                                ) : (
                                    "Submit"
                                )}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}